import dayjs from 'dayjs';
import { CONSULTATION_SLOT_DEFS, SLOT_STARTS } from '@/constants/consultantScheduleSlots';

/** @typedef {'on'|'off'|'locked'|'disabled'|'policy'} SlotState */
/** @typedef {{ time: string, state: SlotState, meta?: string }} SlotRow */

export function isConsultationBlackoutAt(date, clockHour) {
    const weekday = dayjs(date).day();
    if (weekday === 0 || weekday === 6) {
        return clockHour >= 9 && clockHour <= 18;
    }
    if (weekday === 1 && clockHour >= 9 && clockHour < 12) return true;
    if (weekday === 5 && clockHour >= 17) return true;
    return false;
}

export function isWeekendBlackoutDay(date) {
    const wd = dayjs(date).day();
    return wd === 0 || wd === 6;
}

function slotHourFromStart(startTime) {
    const [h] = String(startTime).split(':').map(Number);
    return h;
}

function timeKeyFromStartsAt(startsAt) {
    return dayjs(startsAt).format('HH:mm');
}

function dateKeyFromStartsAt(startsAt) {
    return dayjs(startsAt).format('YYYY-MM-DD');
}

/**
 * @param {unknown[]} slotsRaw
 * @param {Record<string, { student_name?: string }>} bookingsByKey
 */
export function mapSlotsToRowsForDate(slotsRaw, dateStr, bookingsByKey = {}) {
    const daySlots = (slotsRaw || [])
        .filter((s) => dateKeyFromStartsAt(s.starts_at) === dateStr)
        .sort(
            (a, b) =>
                dayjs(a.starts_at).valueOf() - dayjs(b.starts_at).valueOf()
        );

    const byTime = new Map();
    for (const slot of daySlots) {
        const t = timeKeyFromStartsAt(slot.starts_at);
        const key = `${dateStr} ${t}`;
        const booking = bookingsByKey[key];
        const used = Number(slot.capacity_used ?? 0) > 0;

        const adminScheduled = slot.schedule_source === 'admin';

        if (booking || used) {
            byTime.set(t, {
                time: t,
                state: 'locked',
                meta: booking?.student_name
                    ? `已預約 · ${booking.student_name}`
                    : '已預約',
                adminScheduled,
                versionId: booking?.version_id ?? null,
                versionName: booking?.version_name ?? null
            });
            continue;
        }
        if (slot.status === 'closed') {
            byTime.set(t, { time: t, state: 'disabled', meta: '不可排' });
            continue;
        }
        byTime.set(t, { time: t, state: 'on', meta: '開放', adminScheduled });
    }

    for (const def of CONSULTATION_SLOT_DEFS) {
        const key = `${dateStr} ${def.start}`;
        const booking = bookingsByKey[key];
        if (!booking) continue;
        const existing = byTime.get(def.start);
        if (existing?.state === 'locked') continue;
        byTime.set(def.start, {
            time: def.start,
            state: 'locked',
            meta: booking.student_name
                ? `已預約 · ${booking.student_name}`
                : '已預約',
            adminScheduled: existing?.adminScheduled ?? false,
            versionId: booking.version_id ?? null,
            versionName: booking.version_name ?? null
        });
    }

    const rows = [];
    const day = dayjs(dateStr);
    for (const def of CONSULTATION_SLOT_DEFS) {
        const h = slotHourFromStart(def.start);
        if (isConsultationBlackoutAt(day, h)) {
            rows.push({ time: def.start, state: 'policy', meta: '' });
            continue;
        }
        const existing = byTime.get(def.start);
        if (existing) {
            rows.push(existing);
            continue;
        }
        rows.push({ time: def.start, state: 'off', meta: '關閉' });
    }
    return rows;
}

/** @param {SlotRow[]} rows */
export function applyOverridesToRows(rows, dateKey, overrides) {
    const inner = overrides.get(dateKey);
    if (!inner) return rows;
    return rows.map((row) => {
        if (row.state !== 'on' && row.state !== 'off') return row;
        if (!inner.has(row.time)) return row;
        const open = Boolean(inner.get(row.time));
        return {
            ...row,
            state: open ? 'on' : 'off',
            meta: open ? '開放' : '關閉'
        };
    });
}

function emptyPattern() {
    return Array(SLOT_STARTS.length).fill('');
}

/** 月曆摘要：open / booked / blocked */
export function summarizeMonth(
    slotsRaw,
    overrides,
    monthStart,
    monthEnd,
    bookingsByKey = {}
) {
    const map = {};
    let cursor = monthStart.startOf('day');
    const end = monthEnd.startOf('day');
    while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
        const dk = cursor.format('YYYY-MM-DD');
        const rows = applyOverridesToRows(
            mapSlotsToRowsForDate(slotsRaw, dk, bookingsByKey),
            dk,
            overrides
        );
        if (isWeekendBlackoutDay(cursor)) {
            let booked = 0;
            for (const def of CONSULTATION_SLOT_DEFS) {
                const key = `${dk} ${def.start}`;
                if (bookingsByKey[key]) booked += 1;
            }
            map[dk] = { open: 0, booked, blocked: true, pattern: emptyPattern() };
        } else {
            let open = 0;
            let booked = 0;
            const pattern = emptyPattern();
            rows.forEach((row, idx) => {
                if (row.state === 'on') {
                    open++;
                    pattern[idx] = 'open';
                } else if (row.state === 'locked') {
                    booked++;
                    pattern[idx] = 'booked';
                } else if (row.state === 'policy' || row.state === 'disabled') {
                    pattern[idx] = 'blocked';
                }
            });
            map[dk] = { open, booked, blocked: false, pattern };
        }
        cursor = cursor.add(1, 'day');
    }
    return map;
}

function isRowOpen(row) {
    return row.state === 'on' || row.state === 'locked';
}

function effectiveOpen(dateKey, time, baselineRows, overrides) {
    const row = baselineRows.find((r) => r.time === time);
    const base = row ? isRowOpen(row) : false;
    const inner = overrides.get(dateKey);
    if (inner?.has(time)) return Boolean(inner.get(time));
    return base;
}

function slotOrderIndex(startTime) {
    return SLOT_STARTS.indexOf(startTime);
}

function groupConsecutive(sortedStarts) {
    const segments = [];
    for (const t of sortedStarts) {
        const last = segments[segments.length - 1];
        const prev = last?.[last.length - 1];
        if (
            last &&
            prev !== undefined &&
            slotOrderIndex(t) === slotOrderIndex(prev) + 1
        ) {
            last.push(t);
        } else {
            segments.push([t]);
        }
    }
    return segments;
}

function segmentToRule(weekday, segment, effFrom, effTo) {
    const startDef = CONSULTATION_SLOT_DEFS.find((d) => d.start === segment[0]);
    const endDef = CONSULTATION_SLOT_DEFS.find(
        (d) => d.start === segment[segment.length - 1]
    );
    return {
        weekday,
        start_time: startDef?.start ?? segment[0],
        end_time: endDef?.end ?? segment[segment.length - 1],
        effective_from: effFrom,
        effective_to: effTo
    };
}

/**
 * 由月曆勾選狀態產生 booking rules（與諮詢師端策略一致：以週為 effective 區間）
 */
export function buildMonthRulesFromState(displayMonth, slotsRaw, overrides) {
    const monthStart = displayMonth.startOf('month');
    const monthEnd = displayMonth.endOf('month');
    const rules = [];

    let weekStart = monthStart.startOf('week');
    while (!weekStart.isAfter(monthEnd, 'month')) {
        const weekEnd = weekStart.endOf('week');
        const effFrom = weekStart.isBefore(monthStart)
            ? monthStart.format('YYYY-MM-DD')
            : weekStart.format('YYYY-MM-DD');
        const effTo = weekEnd.isAfter(monthEnd)
            ? monthEnd.format('YYYY-MM-DD')
            : weekEnd.format('YYYY-MM-DD');

        for (let w = 0; w <= 6; w++) {
            const openStarts = new Set();
            let cursor = weekStart;
            while (!cursor.isAfter(weekEnd, 'day')) {
                if (cursor.day() !== w) {
                    cursor = cursor.add(1, 'day');
                    continue;
                }
                if (cursor.isBefore(monthStart, 'day') || cursor.isAfter(monthEnd, 'day')) {
                    cursor = cursor.add(1, 'day');
                    continue;
                }
                const dk = cursor.format('YYYY-MM-DD');
                const baseline = mapSlotsToRowsForDate(slotsRaw, dk);
                for (const def of CONSULTATION_SLOT_DEFS) {
                    const h = slotHourFromStart(def.start);
                    if (isConsultationBlackoutAt(cursor, h)) continue;
                    if (effectiveOpen(dk, def.start, baseline, overrides)) {
                        openStarts.add(def.start);
                    }
                }
                cursor = cursor.add(1, 'day');
            }
            if (openStarts.size === 0) continue;
            const sorted = [...openStarts].sort(
                (a, b) => slotOrderIndex(a) - slotOrderIndex(b)
            );
            for (const seg of groupConsecutive(sorted)) {
                rules.push(segmentToRule(w, seg, effFrom, effTo));
            }
        }
        weekStart = weekStart.add(1, 'week');
    }
    return rules;
}

export function ruleAffectsMonth(rule, monthStart, monthEnd) {
    const ef = dayjs(rule.effective_from).startOf('day');
    const et = rule.effective_to
        ? dayjs(rule.effective_to).startOf('day')
        : null;
    let cursor = monthStart.startOf('day');
    const last = monthEnd.startOf('day');
    while (cursor.isBefore(last) || cursor.isSame(last, 'day')) {
        if (cursor.day() === Number(rule.weekday)) {
            if (!cursor.isBefore(ef, 'day') && (!et || !cursor.isAfter(et, 'day'))) {
                return true;
            }
        }
        cursor = cursor.add(1, 'day');
    }
    return false;
}

export function mergeRulesForPut(existing, monthStart, monthEnd, newMonthRules) {
    const kept = [];
    for (const r of existing) {
        if (!ruleAffectsMonth(r, monthStart, monthEnd)) {
            kept.push({
                weekday: Number(r.weekday),
                start_time: String(r.start_time).slice(0, 5),
                end_time: String(r.end_time).slice(0, 5),
                effective_from: dayjs(r.effective_from).format('YYYY-MM-DD'),
                effective_to: r.effective_to
                    ? dayjs(r.effective_to).format('YYYY-MM-DD')
                    : null
            });
        }
    }
    return [...kept, ...newMonthRules];
}
